import React, { useState } from "react";
import Cropper from "react-easy-crop";
import { Box, IconButton, Tooltip, ToggleButtonGroup, ToggleButton } from "@mui/material";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CropFreeIcon from "@mui/icons-material/CropFree";

const ASPECT_OPTIONS = [
  { label: "Frei", value: "free" },
  { label: "16:9", value: "16:9" },
  { label: "4:3", value: "4:3" },
  { label: "1:1", value: "1:1" },
  { label: "3:4", value: "3:4" },
  { label: "9:16", value: "9:16" },
] as const;

const parseAspect = (value: string): number | undefined => {
  if (value === "free") return undefined;
  const [w, h] = value.split(":").map(Number);
  return w / h;
};

type Props = {
  imagePreview: string;
  onCropComplete: (croppedArea: any, croppedAreaPixels: any) => void;
  rotation?: number;
  onRotationChange?: (rotation: number) => void;
  zoom?: number;
  onZoomChange?: (zoom: number) => void;
};

export const ImageCropper = ({
  imagePreview,
  onCropComplete,
  rotation: controlledRotation,
  onRotationChange,
  zoom: controlledZoom,
  onZoomChange,
}: Props) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [internalZoom, setInternalZoom] = useState(1);
  const [internalRotation, setInternalRotation] = useState(0);
  const [aspectKey, setAspectKey] = useState("free");

  const rotation = controlledRotation ?? internalRotation;
  const setRotation = onRotationChange ?? setInternalRotation;
  const zoom = controlledZoom ?? internalZoom;
  const setZoom = onZoomChange ?? setInternalZoom;

  const isControlled = !!onRotationChange;
  const aspect = parseAspect(aspectKey);

  return (
    <>
      <Cropper
        image={imagePreview}
        crop={crop}
        zoom={zoom}
        rotation={rotation}
        aspect={aspect}
        minZoom={0.5}
        maxZoom={3}
        objectFit="contain"
        // Free-position drag is safe because cropimage.ts clamps pixelCrop to
        // image bounds, so out-of-bounds drag never bakes transparent padding
        // into the saved output (the gray-bars failure mode).
        restrictPosition={false}
        style={{ containerStyle: { backgroundColor: "#262626" } }}
        onCropChange={setCrop}
        onCropComplete={onCropComplete}
        onZoomChange={setZoom}
        onRotationChange={setRotation}
      />
      {isControlled && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <ToggleButtonGroup
            value={aspectKey}
            exclusive
            size="small"
            onChange={(_, val) => { if (val) setAspectKey(val); }}
            sx={{
              bgcolor: "rgba(255,255,255,0.85)",
              borderRadius: 2,
              "& .MuiToggleButton-root": {
                px: 1,
                py: 0.25,
                fontSize: "0.7rem",
                textTransform: "none",
              },
            }}
          >
            {ASPECT_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>
                {opt.value === "free" ? <CropFreeIcon sx={{ fontSize: 16 }} /> : opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              bgcolor: "rgba(255,255,255,0.85)",
              borderRadius: 2,
              px: 1,
              py: 0.5,
            }}
          >
            <Tooltip title="Verkleinern" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => setZoom(Math.max(0.5, +(zoom - 0.1).toFixed(2)))}
                  disabled={zoom <= 0.5}
                >
                  <ZoomOutIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Vergrößern" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => setZoom(Math.min(3, +(zoom + 0.1).toFixed(2)))}
                  disabled={zoom >= 3}
                >
                  <ZoomInIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="90° nach links drehen" arrow>
              <IconButton size="small" onClick={() => setRotation(rotation - 90)}>
                <RotateLeftIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="90° nach rechts drehen" arrow>
              <IconButton size="small" onClick={() => setRotation(rotation + 90)}>
                <RotateRightIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </>
  );
};
